'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from './ui/AppLogo';
import {
  LayoutDashboard,
  Cpu,
  TrendingUp,
  Globe,
  DollarSign,
  Bitcoin,
  Newspaper,
  BriefcaseBusiness,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Shield,
  Crown,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: string;
  badgeType?: 'alert' | 'count' | 'new';
}

const navGroups: { title: string; items: NavItem[] }[] = [
  {
    title: 'Core',
    items: [
      {
        id: 'nav-dashboard',
        label: 'Market Research',
        icon: <LayoutDashboard size={18} />,
        href: '/daily-market-research-dashboard',
      },
      {
        id: 'nav-ai-finder',
        label: 'AI Trade Finder',
        icon: <Cpu size={18} />,
        href: '/ai-trade-finder',
        badge: '12',
        badgeType: 'count',
      },
    ],
  },
  {
    title: 'Markets',
    items: [
      {
        id: 'nav-indian',
        label: 'Indian Market',
        icon: <TrendingUp size={18} />,
        href: '/ai-trade-finder',
      },
      {
        id: 'nav-us',
        label: 'US Market',
        icon: <BarChart3 size={18} />,
        href: '/ai-trade-finder',
      },
      {
        id: 'nav-forex',
        label: 'Forex',
        icon: <DollarSign size={18} />,
        href: '/ai-trade-finder',
      },
      {
        id: 'nav-crypto',
        label: 'Crypto',
        icon: <Bitcoin size={18} />,
        href: '/ai-trade-finder',
        badge: 'NEW',
        badgeType: 'new',
      },
      {
        id: 'nav-global',
        label: 'Global Indices',
        icon: <Globe size={18} />,
        href: '/daily-market-research-dashboard',
      },
    ],
  },
  {
    title: 'Research',
    items: [
      {
        id: 'nav-news',
        label: 'News Analysis',
        icon: <Newspaper size={18} />,
        href: '/daily-market-research-dashboard',
        badge: '5',
        badgeType: 'alert',
      },
      {
        id: 'nav-portfolio',
        label: 'Portfolio',
        icon: <BriefcaseBusiness size={18} />,
        href: '/daily-market-research-dashboard',
      },
      {
        id: 'nav-alerts',
        label: 'Alerts',
        icon: <Bell size={18} />,
        href: '/daily-market-research-dashboard',
        badge: '3',
        badgeType: 'alert',
      },
    ],
  },
];

interface SidebarProps {
  activePath?: string;
}

export default function Sidebar({ activePath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className="relative flex flex-col h-full transition-all duration-300 ease-in-out flex-shrink-0 scrollbar-thin overflow-y-auto"
      style={{
        width: collapsed ? '64px' : '240px',
        background: 'var(--surface-1)',
        borderRight: '1px solid var(--border)',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center h-16 px-4 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <AppLogo size={32} />
          {!collapsed && (
            <span
              className="font-semibold text-sm truncate neon-text"
              style={{ letterSpacing: '0.01em' }}
            >
              Trade With Krish
            </span>
          )}
        </div>
      </div>

      {/* Nav Groups */}
      <nav className="flex-1 py-4 px-2 space-y-6">
        {navGroups.map((group) => (
          <div key={`group-${group.title}`}>
            {!collapsed && (
              <p
                className="text-xs font-medium uppercase px-2 mb-2"
                style={{ color: 'var(--muted-foreground)', letterSpacing: '0.08em' }}
              >
                {group.title}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const isActive = activePath === item.href;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative ${
                      isActive
                        ? 'sidebar-active' :'hover:bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full font-mono-nums font-medium flex-shrink-0 ${
                              item.badgeType === 'alert' ?'bg-negative-tint text-negative'
                                : item.badgeType === 'new' ?'badge-momentum' :'bg-positive-tint text-positive'
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && item.badge && (
                      <span
                        className="absolute top-1 right-1 w-2 h-2 rounded-full flex-shrink-0"
                        style={{
                          background:
                            item.badgeType === 'alert' ?'var(--negative)' :'var(--primary)',
                        }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div
        className="px-2 py-4 space-y-0.5"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        {!collapsed && (
          <div
            className="mx-2 mb-3 p-3 rounded-lg"
            style={{ background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.15)' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Crown size={12} style={{ color: 'var(--primary)' }} />
              <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>
                Free Plan
              </span>
            </div>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              3 of 5 daily signals used
            </p>
            <div className="mt-2 h-1 rounded-full" style={{ background: 'var(--muted)' }}>
              <div className="h-1 rounded-full w-3/5" style={{ background: 'var(--primary)' }} />
            </div>
          </div>
        )}

        <Link
          href="/sign-up-login-screen"
          className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 text-muted-foreground hover:bg-muted hover:text-foreground"
          title={collapsed ? 'Upgrade to Pro' : undefined}
        >
          <Shield size={18} style={{ color: 'var(--primary)' }} />
          {!collapsed && <span>Upgrade to Pro</span>}
        </Link>

        <Link
          href="/sign-up-login-screen"
          className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 text-muted-foreground hover:bg-muted hover:text-foreground"
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings size={18} />
          {!collapsed && <span>Settings</span>}
        </Link>

        {/* User */}
        {!collapsed && (
          <div className="flex items-center gap-3 px-2 py-2.5 mt-1">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              K
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium truncate">Krish Mehta</p>
              <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                Free Plan
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-110 z-10"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          color: 'var(--muted-foreground)',
        }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}